import {
  initVersionedSchema,
  toMentionContent,
  toTextContent,
} from '@principle-theorem/editor';
import {
  IDestinationEntityJobRunOptions,
  IHasSourceIdentifier,
  ITranslationMap,
  type FailedDestinationEntityRecord,
  type IDestinationEntity,
  type IDestinationEntityRecord,
  type IGetRecordResponse,
  type IPracticeMigration,
} from '@principle-theorem/practice-migrations/interfaces';
import {
  Interaction,
  ServiceProviderHandler,
  toMention,
} from '@principle-theorem/principle-core';
import {
  InteractionType,
  MentionResourceType,
  type IInteractionV2,
  type IPatient,
} from '@principle-theorem/principle-core/interfaces';
import {
  Firestore,
  WithPrimaryKey,
  getError,
  snapshotCombineLatest,
  type WithRef,
} from '@principle-theorem/shared';
import { compact } from 'lodash';
import { combineLatest, of, type Observable } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { PATIENT_RESOURCE_TYPE } from '../../../destination/entities/patient';
import {
  BasePatientInteractionDestinationEntity,
  IBasePatientInteractionJobData,
  IPatientInteractionMigrationData,
} from '../../../destination/entities/patient-interactions';
import { PatientIdFilter } from '../../../destination/filters/patient-id-filter';
import { PracticeIdFilter } from '../../../destination/filters/practice-id-filter';
import { ItemCodeNoteType } from '../../../mappings/item-codes-to-notes-xlsx';
import { type TranslationMapHandler } from '../../../translation-map';
import { D4WPatient, PatientSourceEntity } from '../../source/entities/patient';
import { PatientCommunicationSourceEntity } from '../../source/entities/patient-communication';
import { PatientCommunicationReplySourceEntity } from '../../source/entities/patient-communication-reply';
import {
  D4WPatientTreatmentNote,
  PatientTreatmentNoteSourceEntity,
} from '../../source/entities/patient-treatment-note';
import { D4WItemCodeToNoteMappingHandler } from '../mappings/item-code-to-note';
import { PatientDestinationEntity } from './patients';
import { PatientHistoryNoteSourceEntity } from '../../source/entities/patient-history-notes';

export interface IPatientInteractionJobData
  extends IBasePatientInteractionJobData<D4WPatient> {
  sourceItemCodes: WithPrimaryKey<ITranslationMap<object, ItemCodeNoteType>>[];
}

export class PatientInteractionDestinationEntity extends BasePatientInteractionDestinationEntity<
  D4WPatient,
  IPatientInteractionJobData
> {
  patientSourceEntity = new PatientSourceEntity();

  override filters = [
    new PracticeIdFilter<IPatientInteractionJobData>((jobData) =>
      jobData.sourcePatient.data.practice_id.toString()
    ),
    new PatientIdFilter<IPatientInteractionJobData>((jobData) =>
      this.sourceEntities.patients
        .getSourceRecordId(jobData.sourcePatient.data)
        .toString()
    ),
  ];

  override sourceEntities = {
    patients: new PatientSourceEntity(),
    treatmentNotes: new PatientTreatmentNoteSourceEntity(),
    patientCommunications: new PatientCommunicationSourceEntity(),
    patientCommunicationReplies: new PatientCommunicationReplySourceEntity(),
    patientHistoryNotes: new PatientHistoryNoteSourceEntity(),
  };

  override destinationEntities = {
    patients: new PatientDestinationEntity(),
  };

  customMappings = {
    itemCodes: new D4WItemCodeToNoteMappingHandler(),
  };

  buildJobData$(
    migration: WithRef<IPracticeMigration>,
    _destinationEntity: WithRef<IDestinationEntity>,
    translationMapHandler: TranslationMapHandler,
    runOptions: IDestinationEntityJobRunOptions
  ): Observable<IPatientInteractionJobData[]> {
    const sourceItemCodes$ = this.customMappings.itemCodes.getRecords(
      translationMapHandler
    );

    return snapshotCombineLatest([sourceItemCodes$]).pipe(
      switchMap((dependencies) =>
        combineLatest([
          this.buildSourceRecordQuery$(
            migration,
            this.sourceEntities.patients,
            runOptions
          ),
          of(dependencies),
        ])
      ),
      map(([patientRecords, [sourceItemCodes]]) =>
        patientRecords.map((sourcePatient) => ({
          sourcePatient,
          sourceItemCodes,
        }))
      )
    );
  }

  async buildMigrationData(
    migration: WithRef<IPracticeMigration>,
    _destinationEntity: WithRef<IDestinationEntity>,
    translationMap: TranslationMapHandler,
    data: IPatientInteractionJobData
  ): Promise<
    | IPatientInteractionMigrationData
    | (IDestinationEntityRecord & FailedDestinationEntityRecord)
  > {
    try {
      const sourcePatientId = data.sourcePatient.data.patient_id.toString();
      const patientRef = await translationMap.getDestination<IPatient>(
        sourcePatientId,
        PATIENT_RESOURCE_TYPE
      );

      if (!patientRef) {
        return this._buildErrorResponse(
          data.sourcePatient.record,
          `Couldn't resolve patient`
        );
      }

      const patient = await Firestore.getDoc(patientRef);

      const d4wInteractions =
        await this.sourceEntities.patientCommunications.filterRecords(
          migration,
          'patientId',
          sourcePatientId
        );

      const interactions = d4wInteractions.map((interaction) => {
        const sourceIdentifier = interaction.record.uid;

        return {
          sourceIdentifier,
          ...Interaction.init({
            type: InteractionType.Sms,
            title: [
              toTextContent(`SMS sent to `),
              toMentionContent(toMention(patient, MentionResourceType.Patient)),
            ],
            createdAt: interaction.record.translations.createdAt,
            content: initVersionedSchema(interaction.data.content || []),
          }),
        };
      });

      const d4wInteractionReplies =
        await this.sourceEntities.patientCommunicationReplies.filterRecords(
          migration,
          'patientId',
          data.sourcePatient.data.patient_id.toString()
        );

      const interactionReplies = d4wInteractionReplies.map((interaction) => {
        const sourceIdentifier = interaction.record.uid;

        return {
          sourceIdentifier,
          ...Interaction.init({
            type: InteractionType.SmsReceived,
            title: [
              toMentionContent(toMention(patient, MentionResourceType.Patient)),
              toTextContent(` replied via SMS`),
            ],
            createdAt: interaction.record.translations.createdAt,
            content: initVersionedSchema(interaction.data.content || []),
          }),
        };
      });

      const notes = await this.sourceEntities.treatmentNotes.filterRecords(
        migration,
        'patientId',
        sourcePatientId
      );

      const treatmentInteractions = buildPatientNotesFromTreatments(
        notes,
        data
      );

      const historyNotes =
        await this.sourceEntities.patientHistoryNotes.filterRecords(
          migration,
          'patientId',
          sourcePatientId
        );

      const historyInteractions = historyNotes
        .map((note) => {
          const medicalNote = note.data.medical_history_note
            ? {
                sourceIdentifier: `${note.record.uid}-medicalNote`,
                ...Interaction.init({
                  type: InteractionType.Note,
                  title: [toTextContent('D4W Medical History')],
                  content: initVersionedSchema(note.data.medical_history_note),
                  pinned: true,
                  createdAt: note.record.translations.createdAt,
                }),
              }
            : undefined;

          const dentalNote = note.data.dental_history_note
            ? {
                sourceIdentifier: `${note.record.uid}-dentalNote`,
                ...Interaction.init({
                  type: InteractionType.Note,
                  title: [toTextContent('D4W Dental History')],
                  content: initVersionedSchema(note.data.dental_history_note),
                  pinned: true,
                  createdAt: note.record.translations.createdAt,
                }),
              }
            : undefined;
          return compact([medicalNote, dentalNote]);
        })
        .flat();

      return {
        patientRef,
        interactions: [
          ...interactions,
          ...interactionReplies,
          ...treatmentInteractions,
          ...historyInteractions,
        ],
      };
    } catch (error) {
      return this._buildErrorResponse(
        data.sourcePatient.record,
        getError(error)
      );
    }
  }
}

function buildPatientNotesFromTreatments(
  notes: IGetRecordResponse<D4WPatientTreatmentNote>[],
  data: IPatientInteractionJobData
): (IInteractionV2 & IHasSourceIdentifier)[] {
  return compact(
    notes.map((note) => {
      const procedureCode = note.data.item_code;
      const code = ServiceProviderHandler.findServiceCode(procedureCode);

      if (code) {
        return;
      }

      const mappedCode = data.sourceItemCodes.find(
        (sourceItemCode) =>
          sourceItemCode.sourceIdentifier === procedureCode &&
          !!sourceItemCode.destinationValue
      );

      if (!mappedCode) {
        return;
      }

      const interactionPartial = {
        content: initVersionedSchema([
          toTextContent(
            `From ${note.data.item_code} ${note.data.tooth_ref ?? ''}${
              note.data.tooth_surface ?? ''
            }`
          ),
          toTextContent(note.data.content || ' '),
        ]),
        createdAt: note.record.translations.createdAt,
      };

      const sourceIdentifier = note.record.uid;

      switch (mappedCode.destinationValue?.value) {
        case ItemCodeNoteType.GeneralPatientNote:
          return {
            sourceIdentifier,
            ...Interaction.init({
              type: InteractionType.Note,
              ...interactionPartial,
            }),
          };
        case ItemCodeNoteType.PatientCallNote:
          return {
            sourceIdentifier,
            ...Interaction.init({
              type: InteractionType.Call,
              ...interactionPartial,
            }),
          };
        case ItemCodeNoteType.PatientEmailNote:
          return {
            sourceIdentifier,
            ...Interaction.init({
              type: InteractionType.Email,
              ...interactionPartial,
            }),
          };
        case ItemCodeNoteType.PatientLabJobNote:
          return {
            sourceIdentifier,
            ...Interaction.init({
              type: InteractionType.LabJob,
              ...interactionPartial,
            }),
          };
        case ItemCodeNoteType.PatientPaymentNote:
          return {
            sourceIdentifier,
            ...Interaction.init({
              type: InteractionType.Payment,
              ...interactionPartial,
            }),
          };
        case ItemCodeNoteType.PatientSMSNote:
          return {
            sourceIdentifier,
            ...Interaction.init({
              type: InteractionType.Sms,
              ...interactionPartial,
            }),
          };
        default:
          return;
      }
    })
  );
}
